import type {
  CustomerAnalyticsMoodysCoordinate,
  CustomerAnalyticsMoodysReportDto,
  CustomerAnalyticsMoodysShapeDto,
} from '@zg-rentals/ts-customer-analytics-client';
import { BoundaryManager, ZillowMapControl } from '@zillow/zillow-map-control';
import '@zillow/zillow-map-control/dist/ZillowMapControl.css';
import { Alert } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

const BOUNDING_PADDING = 50;

export const renderPreviewMarketMap = (shape: CustomerAnalyticsMoodysShapeDto) => {
  const boundingPoly = shape.boundary.flatMap((datPoint) => [datPoint.x, datPoint.y]);
  const customPolygonWkt = `POLYGON ((${boundingPoly}))`;

  const boundingRect = getBoundaryBox(shape);
  return (
    <div style={{ height: '50vw', position: 'relative' }}>
      <ZillowMapControl
        apiKey={__GMAPS_API_KEY__}
        boundingRect={boundingRect}
        boundingPadding={{
          top: BOUNDING_PADDING,
          bottom: BOUNDING_PADDING,
          right: BOUNDING_PADDING,
          left: BOUNDING_PADDING,
        }}
      >
        <BoundaryManager customPolygonWkt={customPolygonWkt} fillCustomRegions={true} />
      </ZillowMapControl>
    </div>
  );
};

export const renderStaticMarketMap = (report: CustomerAnalyticsMoodysReportDto): Promise<string> =>
  new Promise((resolve) => {
    fetchStaticMapUrl([report.shape], '1000x1000')
      .then((result) => result.json())
      .then((result) => fetch(result.signedUrl))
      .then((result) => result.blob())
      .then((imageSrc) => {
        const reader = new FileReader();
        reader.readAsDataURL(imageSrc);
        reader.onloadend = () => {
          if (typeof reader.result !== 'string') return;
          return resolve(reader.result);
        };
        return '';
      })
      .catch((e) => {
        logError({ error: e, context: 'MarketMap renderStaticMarketMap', errorType: 'StaticMapFetchFailure' });
        resolve('');
      });
  });

const getBoundaryBox = (shape: CustomerAnalyticsMoodysShapeDto) => {
  const boundingRect = {
    eastLng: shape.boundary[0].x,
    northLat: shape.boundary[0].y,
    southLat: shape.boundary[0].y,
    westLng: shape.boundary[0].x,
  };

  shape.boundary.forEach((location) => {
    boundingRect.eastLng = Math.max(boundingRect.eastLng, location.x);
    boundingRect.westLng = Math.min(boundingRect.westLng, location.x);
    boundingRect.northLat = Math.max(boundingRect.northLat, location.y);
    boundingRect.southLat = Math.min(boundingRect.southLat, location.y);
  });
  return boundingRect;
};

const filterCoords = (dataCoords: Array<string>, proportionOfUrlLength = 1) => {
  const urlMaxByteLimit = 8000;
  const urlByteLimit = Math.round(urlMaxByteLimit * proportionOfUrlLength);
  const coordSize = dataCoords[0].length;
  const numCoords = dataCoords.length * coordSize;
  const hasTooManyCoords = urlByteLimit - numCoords < 0;

  let filteredCoords = dataCoords;
  if (hasTooManyCoords) {
    const numCoordsToKeep = Math.round(urlByteLimit / coordSize);
    const keepEveryK = Math.round(dataCoords.length / numCoordsToKeep);
    filteredCoords = dataCoords.filter((_, i) => i % keepEveryK === 0);
  }
  return filteredCoords;
};

const fetchStaticMapUrl = async (shape: Array<CustomerAnalyticsMoodysShapeDto>, size = '500x500') => {
  const geoCoords = shape.map((geo) =>
    geo.boundary.map((item: CustomerAnalyticsMoodysCoordinate) => `${item.y.toFixed(4)},${item.x.toFixed(4)}`),
  );

  const geoLengths = geoCoords.map((geo) => geo.length);
  const totalCoords = geoLengths.reduce((prev, cur) => prev + cur, 0);
  const proportionsOfTotal = geoLengths.map((nCoords) => nCoords / totalCoords);

  const largestShapeIndex = geoLengths.indexOf(Math.max(...geoLengths));

  const pathStrings = geoCoords.map(
    (geo, i) => `color:blue|weight:1|fillcolor:blue|${filterCoords(geo, proportionsOfTotal[i]).join('|')}`,
  );
  const combinedPathString = pathStrings.join('&path=');

  const boundingBox = getBoundaryBox(shape[largestShapeIndex]);
  const maxLength = Math.max(boundingBox.eastLng - boundingBox.westLng, boundingBox.northLat - boundingBox.southLat);

  const zoomBuckets = (value: number) => {
    if (value < 0.05) return 13;
    if (value < 0.1) return 12;
    if (value < 0.2) return 11;
    if (value < 0.5) return 10;
    if (value < 2.5) return 8;
    return 7;
  };

  const gmapsAPIParams = new URLSearchParams({
    center: `${shape[largestShapeIndex].center.y.toFixed(4)},${shape[largestShapeIndex].center.x.toFixed(4)}`,
    size,
    zoom: zoomBuckets(maxLength).toString(),
    style: 'feature:poi|visibility:off',
    path: combinedPathString,
  });
  const url = `https://maps.googleapis.com/maps/api/staticmap?` + gmapsAPIParams.toString();
  const endpoint = `${__API__}rental-manager-api/api/v1/multibrandSupport/googleMaps?brandName=zillow`;
  return fetch(endpoint, { method: 'POST', body: JSON.stringify({ url: url }) });
};

export const fetchStaticMap = async (shape: Array<CustomerAnalyticsMoodysShapeDto>): Promise<JSX.Element> => {
  return fetchStaticMapUrl(shape)
    .then((res) => res.json())
    .then((res) => res.signedUrl && <img src={res.signedUrl} />)
    .catch((e) => {
      logError({ error: e, context: 'MarketMap fetchStaticMap', errorType: 'StaticMapFetchFailure' });
      return <Alert tone="critical">Failed to load map. Please try again later.</Alert>;
    });
};
